export type PromoProjectOption = Readonly<{
    id: string
    name: string
    initials: string
    haystack: string
    disabled?: boolean
    disabledReason?: string
}>

export const PROMO_PROJECT_PAGE_SIZES = [4, 8, 16] as const
export const PROMO_PROJECT_DEFAULT_PAGE_SIZE = 4

function normalizeSearch(value: string): string {
    return value.trim().toLowerCase().replace(/ё/g, 'е')
}

export function projectInitials(name: string): string {
    const words = String(name || '')
        .trim()
        .split(/[\s—–/_-]+/)
        .filter(Boolean)
    const letters = words
        .map((word) => {
            const match = word.match(/[0-9A-Za-zА-Яа-яЁё]/)
            return match ? match[0].toUpperCase() : ''
        })
        .filter(Boolean)
    if (letters.length >= 2) return letters.slice(0, 2).join('')
    const compact = String(name || '')
        .replace(/[^0-9A-Za-zА-Яа-яЁё]/g, '')
        .slice(0, 2)
        .toUpperCase()
    return compact || '?'
}

export function ruProjectsCount(n: number): string {
    const n10 = n % 10
    const n100 = n % 100
    if (n10 === 1 && n100 !== 11) return `${n} проект`
    if (n10 >= 2 && n10 <= 4 && (n100 < 12 || n100 > 14)) return `${n} проекта`
    return `${n} проектов`
}

export function buildPromoProjectIndex(
    projects: Array<{ id?: unknown; name?: unknown; projectName?: unknown; [key: string]: unknown }> | null | undefined,
    options?: { disableOnboarding?: boolean }
): readonly PromoProjectOption[] {
    const list = Array.isArray(projects) ? projects : []
    const disableOnboarding = Boolean(options?.disableOnboarding)
    const available: PromoProjectOption[] = []
    const unavailable: PromoProjectOption[] = []
    for (const project of list) {
        const id = String(project?.id || '').trim()
        if (!id) continue
        const name = String(project?.name || project?.projectName || '').trim() || id
        const disabled = disableOnboarding && Boolean(project?.isOnBoardingActive)
        const option = Object.freeze({
            id,
            name,
            initials: projectInitials(name),
            haystack: `${normalizeSearch(name)} ${normalizeSearch(id)}`,
            ...(disabled
                ? { disabled: true, disabledReason: 'Проект еще не настроен' }
                : {}),
        })
        if (disabled) unavailable.push(option)
        else available.push(option)
    }
    return Object.freeze(available.concat(unavailable))
}

export function filterPromoProjects(
    index: readonly PromoProjectOption[],
    query: string
): PromoProjectOption[] {
    const needle = normalizeSearch(query)
    if (!needle) return index.slice()
    return index.filter((item) => item.haystack.includes(needle))
}

export function paginateItems<T>(items: readonly T[], page: number, pageSize: number): T[] {
    const size = pageSize > 0 ? pageSize : PROMO_PROJECT_DEFAULT_PAGE_SIZE
    const totalPages = Math.max(1, Math.ceil(items.length / size) || 1)
    const safePage = Math.min(Math.max(1, page), totalPages)
    const start = (safePage - 1) * size
    return items.slice(start, start + size)
}
